/**
 * mc-shipping-calc.js — Manifesto
 *
 * Widget de cálculo de frete. Custom element <mc-shipping-calc>
 * (snippets/mc-shipping-calc.liquid) com DOIS contextos:
 *
 *   context 'pdp'  (task 0002-pdp-shipping-calculator) — 1 item, cota o
 *                  produto da PDP. Código INTOCADO nesta extensão.
 *   context 'cart' (task 0003-cart-shipping-calculator) — cota o carrinho
 *                  INTEIRO (todas as linhas); usado na gaveta (cart-drawer)
 *                  e na página /cart (main-cart-footer).
 *
 * Ambos chamam diretamente a porta pública da Edge Function jadlog-rates
 * (Supabase) — mesma cubagem/cache do CarrierService do checkout, sem `?key=`.
 *
 * Contrato (fechado com /functions-dev):
 *   POST data-endpoint
 *   pdp:  {"mode":"pdp","cep":"########","items":[{variant_id,quantity,grams,price}]}
 *   cart: {"mode":"cart","cep":"########","items":[{variant_id,quantity,grams,price}, ...]}
 *   200: {"rates":[{service_name,service_code,total_price,currency,delivery_days}]}
 *   rates:[] ou erro de rede/400/429 → mensagem honesta (DP6), nunca preço.
 *
 * Decisões de produto aplicadas no contexto PDP (0002, inalterado):
 *   DP3 — re-cotar (debounce ~400ms) quando o stepper da PDP muda, só se já
 *         houver CEP válido preenchido.
 *   DP5 — CEP persiste em localStorage (`mc_shipping_cep`); auto-cotação no
 *         load quando houver CEP salvo.
 *   DP6 — formato de exibição "Serviço · R$ 0,00 · até N dias úteis".
 *   DP7 — telemetria dataLayer/gtag `shipping_quote_pdp` com CEP truncado a
 *         3 dígitos (LGPD).
 *
 * Decisões de produto aplicadas no contexto CARRINHO (0003):
 *   DP2/DP7 — o footer (gaveta/`.js-contents` da /cart) é recriado a cada
 *         update do Dawn (qty/remoção/add externo) via Section Rendering
 *         API — o widget é destruído e reconectado. Estado mora em storage
 *         (localStorage do CEP + sessionStorage da cotação por assinatura de
 *         carrinho), não no DOM.
 *         IMPORTANTE (correção pós-review, NO-GO B3): os `getSectionsToRender`
 *         do Dawn NÃO cruzam superfícies — mudar qty na gaveta não
 *         re-renderiza main-cart-footer, e vice-versa. Como a gaveta é
 *         global (montada em toda página, inclusive /cart), uma mudança
 *         numa superfície deixava o Total estimado da OUTRA obsoleto. Por
 *         isso o widget TAMBÉM assina `PUB_SUB_EVENTS.cartUpdate` (ver
 *         `connectedCallbackCart`/`onCartUpdatePubSub` abaixo) e recomputa a
 *         cotação (fetch local a /cart.js + assinatura) quando qualquer
 *         superfície publica o evento — mesmo quando o próprio widget não
 *         foi remontado. A subscription é removida no `disconnectedCallback`
 *         (evita leak — `subscribers[]` guarda referência forte ao
 *         callback). Duplicidade de fetch/telemetria entre (a) o remount
 *         "normal" e o pub/sub do MESMO evento, e (b) instâncias simultâneas
 *         (gaveta + /cart) reagindo ao mesmo evento, é absorvida pelo dedup
 *         de módulo (`inFlightCartFetches`/debounce por instância) — ver
 *         nota mais abaixo.
 *   DP2 — auto-cotação no mount se houver CEP salvo; cache client
 *         (sessionStorage, chave `mc_ship_cart:{cep}:{assinatura}`) evita
 *         novo fetch/telemetria quando CEP+composição não mudaram.
 *   DP3 — lista de opções (radiogroup); mais barata selecionada por padrão;
 *         "Total estimado" = data-cart-total (cart.total_price, já líquido
 *         de cupom) + frete selecionado.
 *   DP4 — body {mode:"cart",cep,items:[{variant_id,quantity,grams,price}]}
 *         com grams/price POR UNIDADE (igual ao carrier callback do
 *         checkout) — quantity NÃO é clampada (contrato da porta cart
 *         aceita 1..99, diferente do clamp a 12 da porta pdp).
 *   DP6 — falha/rates:[] → mensagem honesta; a linha "Frete" (elemento
 *         externo, ver data-mc-shipcalc-freight-target) é reposta para o
 *         texto padrão ("calculado no checkout") em todo erro — cobre tanto
 *         o mount inicial (nunca escreveu nada) quanto uma recotação que
 *         falha depois de já ter mostrado um valor válido.
 *   DP8 — telemetria `shipping_quote_cart` com cep_prefix (3 dígitos), cans
 *         (total de latas do carrinho) e line_count (nº de linhas). Só
 *         cotação fresca emite, e só quem de fato DISPAROU o fetch de rede
 *         (não quem apenas reaproveitou um fetch já em voo — ver
 *         `fetchCartRatesDeduped`/`isInitiator` abaixo). Hidratação por
 *         cache client (sessionStorage) nunca emite. Como o dedup é por
 *         fetch-em-voo (não por "já vi esse cacheKey alguma vez"), a
 *         telemetria volta a emitir naturalmente quando o cache expira
 *         (TTL) e uma cotação genuinamente nova é buscada.
 *
 * A gaveta (cart-drawer) é renderizada globalmente (theme.liquid), então na
 * própria página /cart existem DUAS instâncias de <mc-shipping-calc
 * data-context="cart"> montadas ao mesmo tempo (a da gaveta oculta + a da
 * página). Para não duplicar POST à Edge Function nem o evento
 * shipping_quote_cart quando ambas auto-cotam ao montar (ou reagem ao mesmo
 * `PUB_SUB_EVENTS.cartUpdate`), o fetch é deduplicado em estado de MÓDULO
 * (`inFlightCartFetches`, abaixo) — uma única requisição em voo por
 * cacheKey, compartilhada por todas as instâncias da página. A telemetria
 * usa o mesmo mecanismo: só quem criou o fetch (isInitiator) emite.
 *
 * Carregado só onde há o widget (PDP via main-product-manifesto; carrinho
 * via cart-drawer.liquid e main-cart-footer.liquid), nunca globalmente no
 * theme.liquid.
 */

(function () {
  'use strict';

  // Task 0008 (S3): guard de idempotência — evita NotSupportedError se o
  // asset for avaliado 2x (mesmo padrão do mc-cart-discount.js). Zero
  // mudança na lógica de cotação/hooks/Edge Function.
  if (customElements.get('mc-shipping-calc')) return;

  var STORAGE_KEY = 'mc_shipping_cep';
  var QTY_DEBOUNCE_MS = 400;

  var BRL = (window.Intl && Intl.NumberFormat)
    ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;

  function formatBRL(value) {
    if (BRL) return BRL.format(value);
    // Fallback sem Intl (ambientes muito antigos): vírgula decimal manual.
    return 'R$ ' + value.toFixed(2).replace('.', ',');
  }

  function digitsOnly(value) {
    return String(value || '').replace(/\D/g, '').slice(0, 8);
  }

  function maskCep(digits) {
    return digits.length > 5 ? digits.slice(0, 5) + '-' + digits.slice(5) : digits;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var self = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(self, args);
      }, wait);
    };
  }

  function readStoredCep() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return /^\d{8}$/.test(raw) ? raw : null;
    } catch (err) {
      // localStorage indisponível (modo privado, ad-blocker) — segue sem auto-cotação.
      return null;
    }
  }

  function storeCep(digits) {
    try {
      localStorage.setItem(STORAGE_KEY, digits);
    } catch (err) {
      // Sem persistência — próxima PDP pede o CEP de novo.
    }
  }

  // ─── Helpers do contexto CARRINHO (task 0003) ───

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_CACHE_TTL_MS = 10 * 60 * 1000; // 10 min — re-hidratação client; fonte de verdade é o cache 1h da function.
  var CART_FETCH_TIMEOUT_MS = 10 * 1000; // aborta requisição pendurada — sem isso, inFlightCartFetches[cacheKey] travaria indefinidamente.

  // A gaveta (cart-drawer) é renderizada globalmente (theme.liquid) e por isso
  // convive na mesma página com o widget da /cart — as DUAS instâncias montam
  // e auto-cotam ao mesmo tempo quando há CEP salvo (e também podem reagir
  // juntas ao mesmo PUB_SUB_EVENTS.cartUpdate — ver connectedCallbackCart).
  // Sem dedup, isso dispara 2 POSTs simultâneos à Edge Function e 2 eventos
  // `shipping_quote_cart` para a MESMA cotação. `inFlightCartFetches` (estado
  // de módulo, compartilhado por todas as instâncias de
  // <mc-shipping-calc data-context="cart"> na página) resolve isso: uma
  // única requisição em voo por cacheKey; quem chega depois reaproveita a
  // mesma Promise em vez de abrir um novo POST — sem precisar de
  // AbortController por instância para essa coordenação.
  //
  // `isInitiator` (true só para quem de fato criou a entrada em
  // inFlightCartFetches, false para quem só reaproveitou) é o que permite
  // dedup de telemetria SEM precisar de um registro permanente "já vi esse
  // cacheKey": quando o cache expira (TTL) e uma cotação genuinamente nova é
  // buscada, o cacheKey correspondente já não está mais em
  // inFlightCartFetches, então o próximo fetch tem um isInitiator:true de
  // novo e a telemetria volta a emitir (corrige NO-GO R1 — a versão anterior
  // usava um Set permanente que bloqueava reemissão para sempre).
  var inFlightCartFetches = {};

  // ─── Task 0006 (F1): persistência da abertura MANUAL do disclosure ───
  // "CUPOM E FRETE" (cart-drawer.liquid, <details class="mc-cd__tools">)
  // através do re-render do footer/drawer__inner (Section Rendering API —
  // o <details> é RECRIADO a cada qty change, remoção e aplicar/remover
  // cupom). Flag de MÓDULO, em memória — AJ-2 do /ui-ux: NÃO usar
  // sessionStorage (sobreviveria ao reload e violaria a DP2/escopo "sessão
  // de carrinho corrente" da task). Atualizada só pelo evento nativo
  // 'toggle' do próprio <details> (McShippingCalc#bindToolsDisclosure,
  // abaixo) — cobre tanto a abertura/fechamento manual do usuário quanto o
  // auto-close de sucesso já existente (applyRatesCart seta
  // `toolsDetails.open = false`, o que também dispara 'toggle' → a flag
  // acompanha e o painel NÃO reabre sozinho depois, coerente com a DP7 da
  // 0005). NUNCA setada para true por regra de negócio — só ecoa o estado
  // real do DOM que o próprio usuário produziu.
  var toolsManuallyOpen = false;

  // Reset quando o carrinho esvazia: em cart vazio, mc-shipping-calc.liquid
  // NÃO renderiza o widget context 'cart' (ver `sc_render`), então nenhuma
  // instância monta para "ver" a transição — por isso o reset roda aqui, em
  // nível de módulo, assinando o pub/sub nativo do Dawn diretamente (fonte
  // única do item_count em qualquer superfície — gaveta ou /cart). Sem
  // isto, reencher o carrinho reabriria o disclosure sozinho na próxima
  // montagem (auto-open indevido, contra a DP7 da 0005).
  if (typeof window.subscribe === 'function') {
    var toolsResetEventName =
      (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
    window.subscribe(toolsResetEventName, function (evt) {
      if (evt && evt.cartData && evt.cartData.item_count === 0) {
        toolsManuallyOpen = false;
      }
    });
  }

  // ─── Task 0006 (F2): estado "calculando" da linha Total durante a
  // re-hidratação do frete ───
  // Flags de MÓDULO: a instância "antiga" grava (updateTotalCart/
  // hideResultsCart), a instância NOVA lê no init, ANTES do fetch
  // assíncrono de /cart.js, para segurar o último valor bom em vez de
  // piscar para cart.total_price sem frete. AJ-1 do /ui-ux: SEM rótulo
  // textual "calculando…" — o tratamento é só o valor anterior esmaecido +
  // pulsando (classe .is-recalculating, ver mc-cart-drawer.css).
  var lastCartFreightActive = false;
  var lastCartTotalText = '';
  var RECALC_GUARD_MS = 8000; // guard anti-loop — sem loading infinito.

  function fetchCartRatesDeduped(endpoint, cepDigits, items, cacheKey) {
    var existing = inFlightCartFetches[cacheKey];
    if (existing) {
      return existing.then(function (rates) { return { rates: rates, isInitiator: false }; });
    }

    var controller = (window.AbortController) ? new AbortController() : null;
    var timeoutId = controller
      ? setTimeout(function () { controller.abort(); }, CART_FETCH_TIMEOUT_MS)
      : null;

    var fetchOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode: 'cart', cep: cepDigits, items: items }),
    };
    if (controller) fetchOptions.signal = controller.signal;

    var promise = fetch(endpoint, fetchOptions)
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return (data && data.rates) || [];
      })
      .finally(function () {
        if (timeoutId) clearTimeout(timeoutId);
        delete inFlightCartFetches[cacheKey];
      });

    inFlightCartFetches[cacheKey] = promise;
    return promise.then(function (rates) { return { rates: rates, isInitiator: true }; });
  }

  // Assinatura estável da composição do carrinho (variant_id:quantity, ordenados).
  function buildCartSignature(items) {
    return items
      .map(function (i) { return { id: i.variant_id, qty: i.quantity }; })
      .sort(function (a, b) { return (a.id > b.id) ? 1 : (a.id < b.id ? -1 : 0); })
      .map(function (p) { return p.id + ':' + p.qty; })
      .join('|');
  }

  function readCartRatesCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.rates || !parsed.ts) return null;
      if (Date.now() - parsed.ts > CART_CACHE_TTL_MS) return null;
      return parsed;
    } catch (err) {
      return null;
    }
  }

  function writeCartRatesCache(key, rates) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ rates: rates, ts: Date.now() }));
    } catch (err) {
      // sessionStorage indisponível — sem cache client; cada mount refaz o fetch.
    }
  }

  // Lê o carrinho real via AJAX Cart API — grams/price por unidade (DP4),
  // igual ao que o CarrierService recebe no checkout.
  //
  // Task 0010 (DP-8/F2, fix cirúrgico): o ingresso de evento de Harmonização
  // é um produto SEM ENVIO (`requires_shipping: false` no variant) — a Edge
  // Function jadlog-rates cota latas via cubagem por caixas 4/6/12; um item
  // não-físico no payload seria enviado à cotação Jadlog (grams 0,
  // comportamento não testado). Filtramos por `requires_shipping !== false`
  // ANTES de montar items/cans (cubagem) — igual ao que o checkout Shopify já
  // faz nativamente para o frete real. `totalCents` continua vindo de
  // cart.total_price (CARRINHO INTEIRO, com o ingresso incluso) — o Total
  // exibido é sempre subtotal completo + frete só das linhas físicas (DP-8,
  // critério 12). Único ponto tocado neste arquivo; Edge Function intocada.
  function fetchCartItems() {
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        var physicalItems = (cart.items || []).filter(function (i) {
          return i.requires_shipping !== false;
        });
        var items = physicalItems.map(function (i) {
          return {
            variant_id: i.variant_id,
            quantity: i.quantity,
            grams: i.grams,
            price: i.price,
          };
        });
        var cans = items.reduce(function (sum, it) { return sum + (it.quantity || 0); }, 0);
        // total_price (centavos) acompanha os items: o "Total estimado" (DP3)
        // precisa do subtotal da MESMA composição cotada — o data-cart-total
        // do mount fica obsoleto na superfície que não foi remontada (ex.:
        // gaveta aberta depois de mudar qty pela /cart).
        return {
          items: items,
          cans: cans,
          lineCount: items.length,
          totalCents: parseInt(cart.total_price, 10) || 0,
        };
      });
  }

  function escapeHtml(value) {
    var map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) { return map[c]; });
  }

  customElements.define(
    'mc-shipping-calc',
    class McShippingCalc extends HTMLElement {
      connectedCallback() {
        // context 'cart' (task 0003) tem fluxo próprio, totalmente separado —
        // o caminho abaixo (context 'pdp', task 0002) permanece intocado.
        if (this.dataset.context === 'cart') {
          this.connectedCallbackCart();
          return;
        }

        var d = this.dataset;
        this.endpoint = d.endpoint || '';
        this.variantId = d.variantId;
        this.grams = parseInt(d.grams, 10) || 0;
        this.price = parseInt(d.price, 10) || 0;

        this.strings = {
          cepInvalid: d.strCepInvalid || 'CEP inválido',
          calculate: d.strCalculate || 'Calcular',
          calculating: d.strCalculating || 'Calculando…',
          error: d.strError || 'Não foi possível calcular agora — o frete exato aparece no checkout.',
          daysSingular: d.strDaysSingular || 'até %DAYS% dia útil',
          daysPlural: d.strDaysPlural || 'até %DAYS% dias úteis',
        };

        this.form = this.querySelector('[data-mc-shipcalc-form]');
        this.cepInput = this.querySelector('[data-mc-shipcalc-input]');
        this.submitBtn = this.querySelector('[data-mc-shipcalc-submit]');
        this.submitLabel = this.querySelector('[data-mc-shipcalc-submit-label]');
        this.resultEl = this.querySelector('[data-mc-shipcalc-result]');
        this.serviceEl = this.querySelector('[data-mc-shipcalc-service]');
        this.priceEl = this.querySelector('[data-mc-shipcalc-price]');
        this.daysEl = this.querySelector('[data-mc-shipcalc-days]');
        this.errorEl = this.querySelector('[data-mc-shipcalc-error]');

        this.controller = null;

        this.onSubmit = this.onSubmit.bind(this);
        this.onCepInput = this.onCepInput.bind(this);
        this.onQtyChange = debounce(this.onQtyChange.bind(this), QTY_DEBOUNCE_MS);

        if (this.form) this.form.addEventListener('submit', this.onSubmit);
        if (this.cepInput) this.cepInput.addEventListener('input', this.onCepInput);

        this.bindQtyInput();

        // DP5 — auto-cotação no load se houver CEP salvo válido.
        var stored = readStoredCep();
        if (stored && this.cepInput) {
          this.cepInput.value = maskCep(stored);
          this.quote(stored);
        }
      }

      disconnectedCallback() {
        if (this.controller) this.controller.abort();
        if (this.qtyInput) this.qtyInput.removeEventListener('input', this.onQtyChange);
        if (this.qtyInput) this.qtyInput.removeEventListener('change', this.onQtyChange);

        if (this.dataset.context === 'cart') this.disconnectedCallbackCart();
      }

      // ═══════════════════════════════════════════════════════════════
      // Contexto CARRINHO (task 0003) — métodos próprios, não compartilham
      // estado/lógica com o contexto pdp acima (só reusam os helpers puros
      // debounce/digitsOnly/maskCep/formatBRL/readStoredCep/storeCep/
      // currentCepDigits/setLoading/onCepInput, que são context-agnósticos).
      // ═══════════════════════════════════════════════════════════════

      connectedCallbackCart() {
        var d = this.dataset;
        this.endpoint = d.endpoint || '';
        this.cartTotalCents = parseInt(d.cartTotal, 10) || 0;

        this.strings = {
          cepInvalid: d.strCepInvalid || 'CEP inválido',
          calculate: d.strCalculate || 'Calcular',
          calculating: d.strCalculating || 'Calculando…',
          error: d.strError || 'Não foi possível calcular agora — o frete exato aparece no checkout.',
          daysSingular: d.strDaysSingular || 'até %DAYS% dia útil',
          daysPlural: d.strDaysPlural || 'até %DAYS% dias úteis',
          // Prefixo do span de frete no summary do <details> "CUPOM E FRETE"
          // (task 0005) — "%PRICE%" é substituído pelo valor formatado.
          freightSummary: d.strFreightSummary || 'Frete: %PRICE%',
          optionsLabel: d.strOptionsLabel || 'Opções de entrega',
        };

        this.form = this.querySelector('[data-mc-shipcalc-form]');
        this.cepInput = this.querySelector('[data-mc-shipcalc-input]');
        this.submitBtn = this.querySelector('[data-mc-shipcalc-submit]');
        this.submitLabel = this.querySelector('[data-mc-shipcalc-submit-label]');
        this.optionsEl = this.querySelector('[data-mc-shipcalc-options]');
        this.totalEl = this.querySelector('[data-mc-shipcalc-total]');
        this.totalValueEl = this.querySelector('[data-mc-shipcalc-total-value]');
        this.errorEl = this.querySelector('[data-mc-shipcalc-error]');
        // Elemento EXTERNO ao widget (ex.: <span class="mc-cd__freight"> no
        // summary do <details> "CUPOM E FRETE", task 0005) — só existe quando
        // o snippet que envolve o widget o marcou. ESCOPADO ao contêiner de
        // footer mais próximo (B2, correção pós-review): a gaveta é global e
        // convive na /cart com o widget da página — um document.querySelector()
        // global resolveria SEMPRE para o mesmo nó (o da gaveta) nas duas
        // instâncias. `.cart-drawer__footer` é o container real na gaveta;
        // `.cart__footer` na página /cart (que hoje não tem span de frete —
        // freightTarget fica null ali, e updateTotalCart() já trata isso
        // como "não existe", sem erro).
        var freightContainer = this.closest('.cart-drawer__footer, .cart__footer');
        this.freightTarget = freightContainer
          ? freightContainer.querySelector('[data-mc-shipcalc-freight-target]')
          : null;
        // task 0005 (delta) — DP7: <details class="mc-cd__tools"> ancestral,
        // só existe na gaveta (a página /cart não envolve o widget nesse
        // disclosure — ver main-cart-footer.liquid). null ali torna o
        // auto-colapso abaixo (applyRatesCart) um no-op seguro.
        this.toolsDetails = this.closest('.mc-cd__tools');
        // Task 0006 (F1) — reanexa o listener 'toggle' e reaplica a
        // abertura MANUAL do usuário a cada (re)montagem: o <details> acima
        // é recriado a cada re-render do footer/drawer__inner (qty change,
        // remoção, cupom), e este connectedCallback já roda em TODOS esses
        // gatilhos — ponto único e robusto para a persistência.
        this.bindToolsDisclosure();
        // Linha "Total" OFICIAL da gaveta (task 0005, DP4) — hook DISTINTO do
        // botão "Finalizar" (que nunca é tocado por este arquivo; ver DP4-a
        // em cart-drawer.liquid). Também escopado ao mesmo container que o
        // freightTarget — mesma razão (gaveta global + /cart na mesma página).
        this.cdTotalTarget = freightContainer
          ? freightContainer.querySelector('[data-mc-cd-total-value]')
          : null;
        // Task 0019 (DP1/DP2) — número do botão "Finalizar" (gaveta) /
        // "Finalizar compra" (/cart). Hook PRÓPRIO data-mc-cd-checkout-total,
        // ESPELHO da linha Total: recebe o MESMO valor (cart.total_price +
        // frete cotado) e o MESMO tratamento de loading (F2 da 0006). Escopado
        // ao mesmo freightContainer que cdTotalTarget (gaveta global + /cart
        // coexistem — cada instância resolve o hook do SEU container). null
        // seguro quando não existe (carrinho vazio na /cart, ou qualquer
        // superfície sem o hook). Na gaveta o botão é re-renderizado
        // server-side (init Liquid fresca); na /cart o botão vive FORA de
        // .js-contents (nó persistente) — por isso este arquivo é a fonte de
        // verdade do número ali (ver ramo "sem frete" da F2 abaixo). O botão
        // continua um valor ESTIMADO (o Shopify confirma o frete no checkout);
        // a DP4-a/DP-C foram revertidas pela 0019 só na APRESENTAÇÃO.
        this.cdCheckoutTarget = freightContainer
          ? freightContainer.querySelector('[data-mc-cd-checkout-total]')
          : null;
        // Task 0006 (F2) — segura o último Total-com-frete BOM (memória de
        // módulo, gravado pela instância anterior) sincronamente, ANTES do
        // fetch assíncrono de /cart.js abaixo — evita a "piscada" para
        // cart.total_price sem frete enquanto a cotação em cache é
        // re-hidratada. Só quando SABEMOS que havia frete ativo
        // (lastCartFreightActive); sem isso, Total = cart.total_price (SSR,
        // já no DOM) imediatamente, sem loading (DP3 — nunca inventa loading
        // onde não há frete a somar).
        this._recalcTimeoutId = null;
        if (lastCartFreightActive && (this.cdTotalTarget || this.cdCheckoutTarget)) {
          // Task 0019 (DP4) — o botão (cdCheckoutTarget) entra no MESMO
          // anti-piscada da linha Total (cdTotalTarget): segura o último
          // Total-com-frete bom + .is-recalculating até a cotação em cache
          // resolver. UM único guard de 8s cobre os dois alvos (não cria um
          // segundo timer). Cada alvo pode ser null independentemente.
          var totalTargetEl = this.cdTotalTarget;
          var checkoutTargetEl = this.cdCheckoutTarget;
          var originalTotalText = totalTargetEl ? totalTargetEl.textContent : '';
          var originalCheckoutText = checkoutTargetEl ? checkoutTargetEl.textContent : '';
          if (totalTargetEl) {
            totalTargetEl.textContent = lastCartTotalText;
            totalTargetEl.classList.add('is-recalculating');
          }
          if (checkoutTargetEl) {
            checkoutTargetEl.textContent = lastCartTotalText;
            checkoutTargetEl.classList.add('is-recalculating');
          }
          this._recalcTimeoutId = setTimeout(function () {
            // Guard anti-loop — nada resolveu em 8s: sem loading infinito,
            // repõe o fallback (cart.total_price) e limpa o cache de módulo.
            if (totalTargetEl) {
              totalTargetEl.classList.remove('is-recalculating');
              totalTargetEl.textContent = originalTotalText;
            }
            if (checkoutTargetEl) {
              checkoutTargetEl.classList.remove('is-recalculating');
              checkoutTargetEl.textContent = originalCheckoutText;
            }
            lastCartFreightActive = false;
            lastCartTotalText = '';
          }, RECALC_GUARD_MS);
        } else if (this.cdCheckoutTarget) {
          // Task 0019 (DP3 + obsolescência da /cart): SEM frete a somar, o
          // botão é re-hidratado IMEDIATAMENTE com o cart.total_price fresco
          // do widget (cartTotalCents), SEM .is-recalculating (não inventa
          // loading onde não há frete). Crítico na /cart, onde o botão vive
          // FORA de .js-contents (nó persistente): sem este write o número
          // ficaria travado no valor de page-load após um qty change. Na
          // gaveta é redundante-mas-inócuo (o server já renderizou o mesmo).
          this.cdCheckoutTarget.textContent = formatBRL(this.cartTotalCents / 100);
          this.cdCheckoutTarget.classList.remove('is-recalculating');
        }

        this.controller = null;
        this.rates = [];
        this.selectedIndex = 0;
        this._uid = 'mc-shipcalc-cart-' + Math.random().toString(36).slice(2, 8);

        this.onSubmit = this.onCartSubmit.bind(this);
        this.onCepInput = this.onCepInput.bind(this);
        this.onOptionChange = this.onOptionChangeCart.bind(this);
        this.onCartUpdatePubSub = this.onCartUpdatePubSub.bind(this);
        this.debouncedFetchQuoteCart = debounce(this.fetchQuoteCart.bind(this), QTY_DEBOUNCE_MS);

        if (this.form) this.form.addEventListener('submit', this.onSubmit);
        if (this.cepInput) this.cepInput.addEventListener('input', this.onCepInput);
        if (this.optionsEl) this.optionsEl.addEventListener('change', this.onOptionChange);

        // B3 (correção pós-review) — os getSectionsToRender do Dawn não
        // cruzam superfícies (qty na gaveta não re-renderiza main-cart-footer
        // e vice-versa) e a gaveta é global, então uma mudança numa
        // superfície deixava o Total estimado da OUTRA obsoleto. Assinar
        // PUB_SUB_EVENTS.cartUpdate cobre esse caso; a subscription é
        // removida em disconnectedCallbackCart (evita leak). Duplicidade com
        // o auto-cotação do próprio mount (mesmo evento, mesma instância) e
        // com a outra instância reagindo ao mesmo evento é absorvida pelo
        // debounce por instância + dedup de módulo (inFlightCartFetches).
        this.cartUpdateUnsubscribe = null;
        if (typeof window.subscribe === 'function') {
          var cartUpdateEvent =
            (typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) || 'cart-update';
          this.cartUpdateUnsubscribe = window.subscribe(cartUpdateEvent, this.onCartUpdatePubSub);
        }

        // DP2/DP5 — auto-cotação se houver CEP salvo (mesma chave da 0002).
        var stored = readStoredCep();
        if (stored && this.cepInput) {
          this.cepInput.value = maskCep(stored);
          this.quoteCart(stored, { auto: true });
        }
      }

      disconnectedCallbackCart() {
        if (this.cartUpdateUnsubscribe) {
          this.cartUpdateUnsubscribe();
          this.cartUpdateUnsubscribe = null;
        }
        if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit);
        if (this.cepInput && this.onCepInput) this.cepInput.removeEventListener('input', this.onCepInput);
        if (this.optionsEl && this.onOptionChange) this.optionsEl.removeEventListener('change', this.onOptionChange);
        // Task 0006 (F1) — o nó antigo do <details> é descartado junto com
        // este disconnect (Section Rendering API recria tudo); remover o
        // listener é higiene (o node some de qualquer forma), não uma
        // correção de leak crítica.
        if (this.toolsDetails && this.onToolsToggle) {
          this.toolsDetails.removeEventListener('toggle', this.onToolsToggle);
        }
        // Task 0006 (F2) — limpa o guard de 8s desta instância. SEM isto, um
        // timeout de uma instância já substituída/destruída poderia disparar
        // depois e resetar lastCartFreightActive/lastCartTotalText por cima
        // de uma instância MAIS NOVA que já resolveu com sucesso.
        if (this._recalcTimeoutId) {
          clearTimeout(this._recalcTimeoutId);
          this._recalcTimeoutId = null;
        }
      }

      // Task 0006 (F1) — (re)anexa o listener 'toggle' do <details
      // class="mc-cd__tools"> ATUAL e reaplica a abertura MANUAL do usuário
      // sincronamente, ANTES do paint (evita flash fechado→aberto). Chamado
      // a cada connectedCallbackCart — cobre TODOS os gatilhos de re-render
      // (qty change, remoção, aplicar/remover cupom), porque este
      // custom element está sempre dentro do <details> recriado por eles.
      // NUNCA abre por regra de negócio — só ecoa o estado real do DOM que
      // o próprio usuário produziu (toolsManuallyOpen, flag de módulo);
      // não reintroduz o auto-open que a DP7 da 0005 removeu.
      bindToolsDisclosure() {
        var details = this.toolsDetails;
        if (!details) return;

        if (toolsManuallyOpen) {
          details.open = true; // sem mover foco — cliente está no stepper
        }

        this.onToolsToggle = function () {
          toolsManuallyOpen = details.open;
        };
        details.addEventListener('toggle', this.onToolsToggle);
      }

      // B3 — reage a mudanças de carrinho que NÃO passaram pelo remount deste
      // widget (ex.: qty mudou na OUTRA superfície). `quoteCart` já lê o
      // carrinho fresco (fetchCartItems) e só de fato re-cota se CEP+
      // assinatura mudaram (cache client) ou dispara fetch deduplicado em
      // nível de módulo — por isso é seguro chamar sempre que há CEP válido,
      // mesmo quando nada mudou de fato.
      onCartUpdatePubSub() {
        var digits = this.currentCepDigits();
        if (digits.length === 8) this.quoteCart(digits, { auto: true });
      }

      onCartSubmit(e) {
        e.preventDefault();
        this.quoteCart(this.currentCepDigits(), { auto: false });
      }

      // Ponto de entrada único: sempre lê o carrinho real (grátis, GET local),
      // calcula a assinatura da composição e só então decide entre cache
      // client instantâneo (sem fetch, sem telemetria) ou cotação nova na
      // Edge Function (debounced quando disparada por auto-cotação).
      quoteCart(cepDigits, opts) {
        opts = opts || {};
        if (!this.endpoint) return;

        if (!/^\d{8}$/.test(cepDigits)) {
          this.showErrorCart(this.strings.cepInvalid);
          return;
        }

        this.clearErrorCart();
        var self = this;

        fetchCartItems()
          .then(function (info) {
            if (!info.items.length) {
              // Carrinho esvaziou via AJAX (ex.: remoção na /cart) sem reload —
              // o guard do Liquid só cobre o carregamento inicial da página.
              self.latestCacheKey = null;
              self.hideResultsCart();
              return;
            }

            // DP3 — re-ancora o subtotal no /cart.js fresco (o data-cart-total
            // lido no mount pode estar obsoleto quando a mudança de composição
            // aconteceu na outra superfície e esta instância não foi remontada).
            self.cartTotalCents = info.totalCents;

            var signature = buildCartSignature(info.items);
            var cacheKey = 'mc_ship_cart:' + cepDigits + ':' + signature;
            self.latestCacheKey = cacheKey;
            var cached = readCartRatesCache(cacheKey);

            if (cached) {
              storeCep(cepDigits);
              self.applyRatesCart(cached.rates, info, cepDigits, { fromCache: true, cacheKey: cacheKey });
              return;
            }

            if (opts.auto) {
              self.debouncedFetchQuoteCart(cepDigits, info, cacheKey);
            } else {
              self.fetchQuoteCart(cepDigits, info, cacheKey);
            }
          })
          .catch(function () {
            self.showErrorCart(self.strings.error);
          });
      }

      // Requisição deduplicada em nível de módulo (ver fetchCartRatesDeduped):
      // a gaveta (global, sempre montada) e o widget da /cart podem coexistir
      // na mesma página e auto-cotar ao mesmo tempo — sem isso, duas
      // instâncias fariam 2 POSTs e emitiriam telemetria em duplicidade para
      // a mesma cotação. `latestCacheKey` descarta respostas obsoletas (ex.:
      // usuário trocou o CEP antes da requisição anterior responder).
      fetchQuoteCart(cepDigits, info, cacheKey) {
        var self = this;
        this.setLoading(true);

        fetchCartRatesDeduped(this.endpoint, cepDigits, info.items, cacheKey)
          .then(function (result) {
            if (self.latestCacheKey !== cacheKey) return; // superada por cotação mais nova
            var rates = result.rates;
            if (!rates.length) {
              self.showErrorCart(self.strings.error);
              return;
            }
            writeCartRatesCache(cacheKey, rates);
            storeCep(cepDigits);
            self.applyRatesCart(rates, info, cepDigits, {
              fromCache: false,
              isInitiator: result.isInitiator,
            });
          })
          .catch(function () {
            if (self.latestCacheKey !== cacheKey) return;
            self.showErrorCart(self.strings.error);
          })
          .finally(function () {
            if (self.latestCacheKey === cacheKey) self.setLoading(false);
          });
      }

      applyRatesCart(rates, info, cepDigits, opts) {
        this.clearErrorCart();
        this.rates = rates;
        this.selectedIndex = this.cheapestIndex(rates);
        this.renderOptionsCart(rates);
        this.updateTotalCart();
        // task 0005 (delta) — DP7: fecha o disclosure "CUPOM E FRETE" assim
        // que a cotação retorna com sucesso (cache ou fresca). Só FECHA
        // (nunca abre) — o Liquid não emite mais `open` condicional, então
        // um re-render server-side (Section Rendering API) já nasce fechado;
        // esta linha cobre o caso de cotar DEPOIS do mount, sem re-render.
        if (this.toolsDetails) this.toolsDetails.open = false;
        // DP8/R1 — só cotação fresca emite telemetria, e só quem de fato
        // iniciou o fetch de rede (isInitiator) — não quem apenas reaproveitou
        // um fetch já em voo (dedup entre gaveta e /cart, ver
        // fetchCartRatesDeduped no topo do arquivo). Como o dedup é por
        // fetch-em-voo (não por registro permanente), a telemetria volta a
        // emitir normalmente após o cache expirar e uma cotação nova ser buscada.
        if (!opts.fromCache && opts.isInitiator) {
          this.trackQuoteCart(rates[this.selectedIndex], info, cepDigits);
        }
      }

      // Esconde opções/total sem exibir mensagem de erro (carrinho vazio não é falha).
      // Também reseta o span de frete do summary (vazio — task 0005, "sem
      // cotação") e a linha Total oficial da gaveta de volta a
      // cart.total_price (sem frete) — DP4/DP6: nunca mostrar preço de
      // contingência.
      hideResultsCart() {
        this.rates = [];
        if (this.optionsEl) {
          this.optionsEl.innerHTML = '';
          this.optionsEl.hidden = true;
        }
        if (this.totalEl) this.totalEl.hidden = true;
        if (this.freightTarget) this.freightTarget.textContent = '';
        if (this.cdTotalTarget) {
          this.cdTotalTarget.textContent = formatBRL(this.cartTotalCents / 100);
          // Task 0006 (F2) — falha/assinatura mudou: resolve o estado de
          // loading (se havia) sem cair num loop; nunca deixa a linha presa
          // em "calculando" por mais tempo que o necessário.
          this.cdTotalTarget.classList.remove('is-recalculating');
        }
        // Task 0019 (DP3/DP6) — repõe o botão "Finalizar" para cart.total_price
        // (sem frete), espelhando o reset da linha Total. Nunca preço de
        // contingência (DP6 da 0003).
        if (this.cdCheckoutTarget) {
          this.cdCheckoutTarget.textContent = formatBRL(this.cartTotalCents / 100);
          this.cdCheckoutTarget.classList.remove('is-recalculating');
        }
        // Task 0006 (F2) — sem frete ativo: limpa o cache de módulo, a
        // PRÓXIMA instância não deve segurar um valor obsoleto.
        lastCartFreightActive = false;
        lastCartTotalText = '';
        if (this._recalcTimeoutId) {
          clearTimeout(this._recalcTimeoutId);
          this._recalcTimeoutId = null;
        }
      }

      // DP3 — default = opção mais barata.
      cheapestIndex(rates) {
        var best = 0;
        var bestPrice = Infinity;
        rates.forEach(function (r, idx) {
          var p = parseInt(r.total_price, 10);
          if (!isNaN(p) && p < bestPrice) {
            bestPrice = p;
            best = idx;
          }
        });
        return best;
      }

      renderOptionsCart(rates) {
        if (!this.optionsEl) return;
        var self = this;
        var html = rates.map(function (rate, idx) {
          var cents = parseInt(rate.total_price, 10);
          var price = isNaN(cents) ? '' : formatBRL(cents / 100);
          var days = parseInt(rate.delivery_days, 10);
          var daysText = '';
          if (!isNaN(days)) {
            var template = days === 1 ? self.strings.daysSingular : self.strings.daysPlural;
            daysText = template.replace('%DAYS%', String(days));
          }
          var checked = idx === self.selectedIndex ? ' checked' : '';
          return (
            '<label class="mc-shipcalc__option">' +
              '<input type="radio" class="mc-shipcalc__option-radio" name="' + self._uid + '" value="' + idx + '"' + checked + '>' +
              '<span class="mc-shipcalc__option-service">' + escapeHtml(rate.service_name || '') + '</span>' +
              '<span class="mc-shipcalc__option-price">' + escapeHtml(price) + '</span>' +
              '<span class="mc-shipcalc__option-days">' + escapeHtml(daysText) + '</span>' +
            '</label>'
          );
        }).join('');
        this.optionsEl.innerHTML = html;
        this.optionsEl.hidden = false;
      }

      onOptionChangeCart(e) {
        var input = e.target && e.target.closest ? e.target.closest('input[type="radio"]') : null;
        if (!input) return;
        var idx = parseInt(input.value, 10);
        if (isNaN(idx) || idx < 0 || idx >= this.rates.length) return;
        this.selectedIndex = idx;
        this.updateTotalCart();
      }

      // DP3 — Total estimado (dentro do widget) = cart.total_price (líquido
      // de cupom) + frete selecionado. Também atualiza (task 0005):
      //   - o span de frete no summary do <details> "CUPOM E FRETE" (DP7/DP1);
      //   - a linha "Total" OFICIAL da gaveta via cdTotalTarget (DP4);
      //   - o número do botão "Finalizar"/"Finalizar compra" via
      //     cdCheckoutTarget (task 0019, DP1/DP2) — hook DISTINTO, MESMO valor
      //     e mesmo tratamento de loading (a DP4-a/DP-C foram revertidas).
      updateTotalCart() {
        if (!this.rates.length) return;
        var rate = this.rates[this.selectedIndex];
        var freightCents = parseInt(rate.total_price, 10) || 0;

        if (this.totalEl && this.totalValueEl) {
          var totalCents = this.cartTotalCents + freightCents;
          this.totalValueEl.textContent = formatBRL(totalCents / 100);
          this.totalEl.hidden = false;
        }
        if (this.freightTarget) {
          this.freightTarget.textContent = this.strings.freightSummary.replace(
            '%PRICE%',
            formatBRL(freightCents / 100)
          );
        }
        if (this.cdTotalTarget) {
          var cdTotalCents = this.cartTotalCents + freightCents;
          var cdTotalText = formatBRL(cdTotalCents / 100);
          this.cdTotalTarget.textContent = cdTotalText;
          // Task 0006 (F2) — sucesso: resolve o loading (se havia) com o
          // valor crisp e grava o valor bom para a PRÓXIMA instância segurar
          // enquanto recota (evita a "piscada" para cart.total_price sem
          // frete no próximo re-render).
          this.cdTotalTarget.classList.remove('is-recalculating');
          lastCartFreightActive = true;
          lastCartTotalText = cdTotalText;
          if (this._recalcTimeoutId) {
            clearTimeout(this._recalcTimeoutId);
            this._recalcTimeoutId = null;
          }
        }
        // Task 0019 (DP1/DP2) — o botão "Finalizar" espelha o MESMO valor da
        // linha Total (cart.total_price + frete). As vars de módulo
        // lastCartFreightActive/lastCartTotalText e o clearTimeout do guard já
        // foram setados uma vez acima (dono único de escrita = a linha Total);
        // o botão só reusa o texto, sem duplicar estado nem timer. Recomputado
        // aqui (não reusa o var hoisted do bloco cdTotalTarget) para ficar
        // correto mesmo no caso improvável de cdCheckoutTarget existir sem
        // cdTotalTarget no mesmo container.
        if (this.cdCheckoutTarget) {
          this.cdCheckoutTarget.textContent = formatBRL((this.cartTotalCents + freightCents) / 100);
          this.cdCheckoutTarget.classList.remove('is-recalculating');
        }
      }

      // DP6 — nunca escreve preço de contingência. hideResultsCart() (task
      // 0005) já cuida de esvaziar o span de frete do summary e de repor a
      // linha Total oficial da gaveta a cart.total_price (sem frete) — o
      // "permanece vazio" do DP6 cobre tanto o mount inicial (nunca houve
      // sucesso) quanto uma recotação que falha depois de já ter mostrado um
      // valor válido.
      showErrorCart(text) {
        this.hideResultsCart();
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = text;
        }
      }

      clearErrorCart() {
        if (this.errorEl) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
      }

      // DP8 — shipping_quote_cart com CEP truncado a 3 dígitos (LGPD).
      trackQuoteCart(rate, info, cepDigits) {
        try {
          var cents = parseInt(rate.total_price, 10) || 0;
          var payload = {
            cep_prefix: cepDigits.slice(0, 3),
            cans: info.cans,
            price_cents: cents,
            service: rate.service_name || rate.service_code || '',
            line_count: info.lineCount,
          };
          if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push(Object.assign({ event: 'shipping_quote_cart' }, payload));
          }
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'shipping_quote_cart', payload);
          }
        } catch (err) {
          // Telemetria nunca deve travar a cotação.
        }
      }

      // Localiza o stepper de quantidade da PDP (data-mc-pdp-qty) — mesmo
      // input que mc-product-page.js usa para o total dinâmico do botão.
      bindQtyInput() {
        var root = this.closest('[data-section-type="main-product-manifesto"]') || document;
        this.qtyInput = root.querySelector('[data-mc-pdp-qty]');
        if (!this.qtyInput) return;
        this.qtyInput.addEventListener('input', this.onQtyChange);
        this.qtyInput.addEventListener('change', this.onQtyChange);
      }

      currentCepDigits() {
        return digitsOnly(this.cepInput ? this.cepInput.value : '');
      }

      currentQty() {
        var raw = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
        if (isNaN(raw) || raw < 1) raw = 1;
        // Contrato da porta pdp: quantity 1..12 — clampe no cliente.
        return Math.min(12, raw);
      }

      onCepInput() {
        this.cepInput.value = maskCep(digitsOnly(this.cepInput.value));
      }

      onSubmit(e) {
        e.preventDefault();
        this.quote(this.currentCepDigits());
      }

      // DP3 — só re-cota se já houver um CEP válido preenchido.
      onQtyChange() {
        var digits = this.currentCepDigits();
        if (digits.length === 8) this.quote(digits);
      }

      setLoading(on) {
        if (this.submitBtn) this.submitBtn.disabled = on;
        if (this.cepInput) this.cepInput.disabled = on;
        if (this.submitLabel) {
          this.submitLabel.textContent = on ? this.strings.calculating : this.strings.calculate;
        }
      }

      clearError() {
        if (this.errorEl) {
          this.errorEl.hidden = true;
          this.errorEl.textContent = '';
        }
      }

      showError(text) {
        if (this.resultEl) this.resultEl.hidden = true;
        if (this.errorEl) {
          this.errorEl.hidden = false;
          this.errorEl.textContent = text;
        }
      }

      showResult(rate) {
        this.clearError();
        if (!this.resultEl) return;
        this.resultEl.hidden = false;
        if (this.serviceEl) this.serviceEl.textContent = rate.service_name || '';

        var cents = parseInt(rate.total_price, 10);
        if (this.priceEl) this.priceEl.textContent = isNaN(cents) ? '' : formatBRL(cents / 100);

        var days = parseInt(rate.delivery_days, 10);
        if (this.daysEl) {
          if (isNaN(days)) {
            this.daysEl.textContent = '';
          } else {
            var template = days === 1 ? this.strings.daysSingular : this.strings.daysPlural;
            this.daysEl.textContent = template.replace('%DAYS%', String(days));
          }
        }
      }

      // DP7 — evento shipping_quote_pdp com CEP truncado a 3 dígitos (LGPD).
      trackQuote(rate, qty, cepDigits) {
        try {
          var cents = parseInt(rate.total_price, 10) || 0;
          var payload = {
            cep_prefix: cepDigits.slice(0, 3),
            cans: qty,
            price_cents: cents,
            service: rate.service_name || rate.service_code || '',
          };
          if (window.dataLayer && typeof window.dataLayer.push === 'function') {
            window.dataLayer.push(Object.assign({ event: 'shipping_quote_pdp' }, payload));
          }
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'shipping_quote_pdp', payload);
          }
        } catch (err) {
          // Telemetria nunca deve travar a cotação.
        }
      }

      quote(cepDigits) {
        if (!this.endpoint) return;

        if (!/^\d{8}$/.test(cepDigits)) {
          this.showError(this.strings.cepInvalid);
          return;
        }

        this.clearError();
        this.setLoading(true);

        if (this.controller) this.controller.abort();
        var controller = (window.AbortController) ? new AbortController() : null;
        this.controller = controller;

        var self = this;
        var qty = this.currentQty();

        var fetchOptions = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mode: 'pdp',
            cep: cepDigits,
            items: [
              {
                variant_id: self.variantId,
                quantity: qty,
                grams: self.grams,
                price: self.price,
              },
            ],
          }),
        };
        if (controller) fetchOptions.signal = controller.signal;

        fetch(this.endpoint, fetchOptions)
          .then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          })
          .then(function (data) {
            var rates = data && data.rates;
            if (!rates || !rates.length) {
              self.showError(self.strings.error);
              return;
            }
            var rate = rates[0];
            self.showResult(rate);
            storeCep(cepDigits);
            self.trackQuote(rate, qty, cepDigits);
          })
          .catch(function (err) {
            // Requisição superada por uma cotação mais nova (qty/CEP mudou) —
            // não é falha, o resultado da requisição vigente ainda vai chegar.
            if (err && err.name === 'AbortError') return;
            self.showError(self.strings.error);
          })
          .finally(function () {
            if (self.controller === controller) self.setLoading(false);
          });
      }
    }
  );
})();
